import { t } from '@lingui/core/macro';
import { DeclinedSubheading } from 'components/ApplicationOverview/DeclinedSubheading/DeclinedSubheading';
import { NoAvailableFundsSubheading } from 'components/ApplicationOverview/NoAvailableFundsSubheading/NoAvailableFundsSubheading';
import { getDefaultRejectReason } from 'components/RejectedOffer/useRejectReason.helpers';
import {
  isOnlyRCFOffer,
  isRCFOffer,
} from 'components/SummaryCard/SummaryCard.helpers';
import { TIntroducerDataContext } from 'contexts/IntroducerData/IntroducerData.context';
import {
  TApplication,
  TIntroducerApplication,
} from 'lendingApi/applications.types';
import { TDeclineReasonsData } from 'lendingApi/lendingApi.types';
import { TApplicationOverviewHeading } from 'types';
import { APPLICATION_STATUS } from '../applications/applications.config';

const getNumberOfOfferColumns = ({
  formal_offer_sets,
  requested_products,
}: TIntroducerApplication) => {
  const formalOffers = formal_offer_sets?.[0]?.formal_offers;

  if (formalOffers?.length > 0) {
    if (formalOffers?.find((item) => item.is_selected)) {
      return 1;
    } else {
      return formalOffers.length;
    }
  }
  return requested_products.length;
};

type TGetApplicationStepData = {
  application: TIntroducerApplication;
  rejectReason?: string;
  declineReasons?: TDeclineReasonsData;
  checkIntroducerPermission: TIntroducerDataContext['checkIntroducerPermission'];
  isDE: boolean;
  hasDocumentFeedback: boolean;
};

export const getApplicationOverviewHeadings = ({
  application,
  rejectReason,
  declineReasons,
  checkIntroducerPermission,
  isDE,
  hasDocumentFeedback,
}: TGetApplicationStepData): TApplicationOverviewHeading | undefined => {
  const { is_partner_channel: isPartnerChannel } = application;

  if (isPartnerChannel) {
    const partnerHeadings = getPartnerHeadings({ application, declineReasons });
    // Only return if there is a partner heading, if not, fallback to broker headings
    if (partnerHeadings?.heading) return partnerHeadings;
  }

  if (shouldShowNoAvailableFunds(application)) {
    return {
      heading: t`Offered with no available funds`,
      subHeading: (
        <NoAvailableFundsSubheading
          offeredAmount={
            application.formal_offer_sets[0]?.formal_offers?.[0]?.max_amount
          }
        />
      ),
    };
  }

  return getBrokerHeadings({
    application,
    rejectReason,
    declineReasons,
    checkIntroducerPermission,
    isDE,
    hasDocumentFeedback,
  });
};

const getPartnerHeadings = ({
  application,
  declineReasons,
}: Pick<TGetApplicationStepData, 'application' | 'declineReasons'>) => {
  const { formal_offer_sets, status: applicationStatus } = application;

  const offers = formal_offer_sets[0]?.formal_offers;

  const hasMultipleUnselectedOffers =
    offers?.length > 1 && !offers.find((offer) => offer.is_selected);

  switch (applicationStatus) {
    case APPLICATION_STATUS.open:
      return {
        heading: t`Continue application`,
        subHeading: `The customer's application is still in progress.`,
      };
    case APPLICATION_STATUS.action_required:
      return {
        heading: 'Documents required',
        subHeading: 'More documents are required from the customer.',
      };
    case APPLICATION_STATUS.declined:
      return {
        heading: 'Application declined',
        subHeading: (
          <DeclinedSubheading
            reasons={declineReasons}
            fallback="The application has been declined."
          />
        ),
      };
    case APPLICATION_STATUS.expired:
      return {
        heading: hasMultipleUnselectedOffers
          ? 'Offers expired'
          : 'Offer expired',
        subHeading: hasMultipleUnselectedOffers
          ? 'The original offers have expired, but the customer can log in and refresh their offers for an updated decision. Additional documents may be required.'
          : 'The original offer has expired, but the customer can log in and refresh their offer for an updated decision. Additional documents may be required.',
      };
    case APPLICATION_STATUS.offered:
      return {
        heading: hasMultipleUnselectedOffers ? 'Review offers' : 'Review offer',
        subHeading: hasMultipleUnselectedOffers
          ? 'The customer has received these offers.'
          : 'The customer has received this offer.',
      };
    case APPLICATION_STATUS.talking_to_customer:
      return {
        heading: 'Applicant funding steps',
        subHeading:
          'The applicant just needs to complete the following funding steps to access their funds.',
      };
  }
};

export const checkApplicationIsComplete = (
  application?: TIntroducerApplication | TApplication,
) => {
  return application?.is_completed;
};

export const shouldShowNoAvailableFunds = (
  application?: TIntroducerApplication,
) => {
  const availableFunds =
    application?.formal_offer_sets?.[0]?.formal_offers?.[0]?.available_funds;
  const hasDrawnDown =
    availableFunds !==
    application?.formal_offer_sets?.[0]?.formal_offers?.[0]?.max_amount;

  return (
    application?.status === 'offered' &&
    hasDrawnDown &&
    availableFunds !== undefined &&
    availableFunds <= 0
  );
};

const getBrokerHeadings = ({
  application,
  rejectReason,
  declineReasons,
  checkIntroducerPermission,
  isDE,
  hasDocumentFeedback,
}: TGetApplicationStepData) => {
  const { formal_offer_sets, status: applicationStatus } = application;

  const offers = formal_offer_sets[0]?.formal_offers;
  const amountOfOffers = offers?.length || 0;
  const multipleOffers =
    amountOfOffers > 1 && !offers?.find((item) => item.is_selected);

  if (
    hasDocumentFeedback &&
    (applicationStatus === APPLICATION_STATUS.manual_review ||
      applicationStatus === APPLICATION_STATUS.action_required)
  ) {
    return {
      heading: t`Documents reviewed`,
      subHeading: t`Your documents have been reviewed. Please upload the required documents in the appropriate section to complete your loan application. You can find detailed feedback in the respective category.`,
    };
  }

  switch (applicationStatus) {
    case APPLICATION_STATUS.open: {
      if (!checkIntroducerPermission('can_create_applications')) {
        return {
          heading: t`Application in progress`,
          subHeading: t`The application is still in the process of being submitted.`,
        };
      }
      if (checkApplicationIsComplete(application)) {
        return {
          heading: t`Documents required`,
          subHeading: t`To complete this application, please upload the required documents and any additional supporting information in the documents tab.`,
        };
      }
      return {
        heading: t`Continue the application`,
        subHeading: t`Pick up where you left off.`,
      };
    }
    case APPLICATION_STATUS.action_required: {
      if (!checkIntroducerPermission('can_upload_broker_documents')) {
        return {
          heading: t`Documents required`,
          subHeading: t`More documents are required to progress the application.`,
        };
      }
      return {
        heading: t`Documents required`,
        subHeading: t`To complete this application, please upload the required documents and any additional supporting information in the documents tab.`,
      };
    }
    case APPLICATION_STATUS.customer_to_link_open_banking: {
      return {
        heading: t`Link open banking`,
        subHeading: t`The application is with your client to link their trading bank account. Notify your client to link their trading account with open banking.`,
      };
    }
    case APPLICATION_STATUS.offer_revoked: {
      return {
        heading: amountOfOffers > 1 ? t`Offers revoked` : t`Offer revoked`,
        subHeading:
          amountOfOffers > 1
            ? t`Unfortunately, the offers for this application have been revoked.`
            : t`Unfortunately, the offer for this application has been revoked.`,
      };
    }
    case APPLICATION_STATUS.manual_review: {
      return {
        heading: t`Manual check`,
        subHeading: t`Our team needs to manually check details in the application before it can be reviewed. Please reach out to us if you have not heard from the team after 24 hours.`,
      };
    }
    case APPLICATION_STATUS.awaiting_underwriter: {
      return {
        heading: t`Awaiting underwriter`,
        subHeading: t`The application is complete and an underwriter will be assigned to review it soon.`,
      };
    }
    case APPLICATION_STATUS.in_underwriting: {
      return {
        heading: t`In review by underwriter`,
        subHeading: t`An underwriter is reviewing the application - we’ll send you an update about it soon.`,
      };
    }
    case APPLICATION_STATUS.declined: {
      return {
        heading: t`Application declined`,
        subHeading: (
          <DeclinedSubheading
            reasons={declineReasons}
            fallback={t`The application has been declined. Where possible, we’ll send details to you by email on why we’ve made this decision.`}
          />
        ),
      };
    }
    case APPLICATION_STATUS.deferred: {
      return {
        heading: t`Application deferred`,
        subHeading: t`We’re currently not able to proceed with this application.`,
      };
    }
    case APPLICATION_STATUS.expired: {
      if (!checkIntroducerPermission('can_refresh_offers')) {
        return {
          heading: t`Refresh`,
          subHeading: t`The original offer has expired. The application may be eligible for resubmission.`,
        };
      }
      return {
        heading: t`Refresh`,
        subHeading: multipleOffers
          ? t`The original offers have expired, but you can resubmit the original application to review the applicant for an updated decision. Additional documents may be required.`
          : t`The original offer has expired, but you can resubmit the original application to review the applicant for an updated decision. Additional documents may be required.`,
      };
    }
    case APPLICATION_STATUS.conditional_offer: {
      return {
        heading:
          amountOfOffers > 1
            ? 'Review conditional offers'
            : 'Review conditional offer',
        subHeading:
          amountOfOffers > 1
            ? "These offers depend on reviewing the applicant's latest VAT return(s), and they may be withdrawn based on that information."
            : "This offer depends on reviewing the applicant's latest VAT return(s), and it may be withdrawn based on that information.",
      };
    }
    case APPLICATION_STATUS.offered: {
      const numberOfColumns = getNumberOfOfferColumns(application);
      const noPermissionSubHeading =
        !checkIntroducerPermission('can_interact_with_offers') &&
        t`These offers are available for your team to review.`;

      const isRCFOnly = isOnlyRCFOffer(offers, isDE);
      const hasBothProductTypes =
        offers.some((o) => isRCFOffer(o, isDE)) &&
        offers.some((o) => !isRCFOffer(o, isDE));

      if (hasBothProductTypes) {
        return {
          heading: t`Congratulations, you have multiple approved financing options!`,
          subHeading:
            noPermissionSubHeading || t`Select an option to see more details.`,
        };
      }

      if (isRCFOnly) {
        return {
          heading: t`Congratulations, we can offer you a flexible revolving credit facility!`,
          subHeading:
            noPermissionSubHeading ||
            t`Your customer receives full access to a flexible credit line for 12 months and only pays interest during this period, with no principal repayment. Withdrawals and repayments are possible at any time free of charge; renewal is possible after a new credit check.`,
        };
      }

      if (numberOfColumns > 1) {
        return {
          heading:
            numberOfColumns === 2
              ? t`Congratulations, you have two approved offers!`
              : t`Congratulations, you have multiple approved offers!`,
          subHeading:
            noPermissionSubHeading ||
            t`Select one to see the early repayment plan below.`,
        };
      } else {
        return {
          heading: t`Congratulations, this is your approved offer!`,
          subHeading:
            noPermissionSubHeading ||
            t`You can see the early repayment plan below.`,
        };
      }
    }
    case APPLICATION_STATUS.offer_rejected: {
      return {
        heading: amountOfOffers > 1 ? t`Offers rejected` : t`Offer rejected`,
        subHeading: rejectReason || getDefaultRejectReason(amountOfOffers > 1),
      };
    }
    case APPLICATION_STATUS.talking_to_customer: {
      return {
        heading: t`Applicant funding steps`,
        subHeading: t`The applicant has accepted an offer and just needs to complete the following funding steps to access funds.`,
      };
    }
    case APPLICATION_STATUS.funded: {
      return {
        heading: t`Offer funded`,
        subHeading: t`Congratulations, the applicant has taken their funds.`,
      };
    }
  }
};
